import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  Timeline as TimelineIcon,
  Warning as WarningIcon,
  Logout as LogoutIcon,
  PeopleAlt as PeopleAltIcon,
} from '@mui/icons-material';
import { sidebarStyles, drawerWidth } from './Sidebar.styles';
import { useAuth } from '../../context/AuthContext';

interface SidebarProps {
  mobileOpen: boolean;
  onDrawerToggle: () => void;
}

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Inventory', icon: <InventoryIcon />, path: '/inventory' },
  { text: 'Activity Log', icon: <TimelineIcon />, path: '/activity-log' },
  { text: 'Limbo Items', icon: <WarningIcon />, path: '/limbo-items' },
  { text: 'Users', icon: <PeopleAltIcon />, path: '/users' },
];

export default function Sidebar({ mobileOpen, onDrawerToggle }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { user, signOut } = useAuth();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  // Sync selectedIndex with current route
  useEffect(() => {
    const currentIndex = menuItems.findIndex((item) => item.path === location.pathname);
    if (currentIndex !== -1) {
      setSelectedIndex(currentIndex);
    }
  }, [location.pathname]);

  const handleListItemClick = (index: number, path: string) => {
    setSelectedIndex(index);
    navigate(path);
    // Close mobile drawer when item is clicked
    if (mobileOpen) {
      onDrawerToggle();
    }
  };

  const drawerContent = (
    <Box sx={sidebarStyles.container}>
      {/* Header */}
      <Box sx={sidebarStyles.header}>
        <Typography variant="h5">Crayons to</Typography>
        <Typography variant="h5">Classrooms</Typography>
      </Box>

      <Divider sx={{ bgcolor: 'rgba(255, 255, 255, 0.12)' }} />

      {/* Navigation Menu */}
      <List sx={{ flexGrow: 1, pt: 2 }}>
        {menuItems.map((item, index) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              selected={selectedIndex === index}
              onClick={() => handleListItemClick(index, item.path)}
              sx={sidebarStyles.listItemButton}
            >
              <ListItemIcon
                sx={{ color: selectedIndex === index ? 'primary.main' : 'inherit', minWidth: 40 }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ bgcolor: 'rgba(255, 255, 255, 0.12)' }} />

      {/* User Profile / Logout Section */}
      <Box sx={sidebarStyles.userSection}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {user?.email ?? 'Admin'}
          </Typography>
        </Box>
        <ListItemButton onClick={handleSignOut} sx={{ p: 0.5, borderRadius: 1, minWidth: 0 }}>
          <LogoutIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 20 }} />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
      {/* Mobile Drawer - Temporary */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer - Persistent */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
}
